import { useCallback, useEffect, useState } from 'react'

import {
  IconSegment,
  IconUserCircle,
  LinkButton,
  Popover,
  SearchButton,
  Select
} from '@smartbear/swagger-portal-component-library'
import { clsx } from 'clsx'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router'
import { useHydrated } from 'remix-utils/use-hydrated'

import { SearchModalDesktop } from '../SearchModal/desktop'
import { DarkModeToggleLoggedOut } from '~/portal/components/DarkModeToggleLoggedOut'
import { useApiHubPlan } from '~/portal/hooks/useApiHubPlan'
import { usePageLayout } from '~/portal/hooks/usePageLayout'
import { CONSUMER_PATHNAMES } from '~/portal/routes/sp-portal/constants'
import { FeatureFlags } from '~/portal/types/flags'
import { LandingProduct } from '~/portal/types/product'
import { DebugUI } from '~/shared/components/DebugUI'
import { Profile } from '~/shared/components/Profile'
import { UserInfo } from '~/shared/types/user.ts'

export interface TopBarDesktopProps {
  accentColor?: string,
  logo?: string,
  name?: string,
  user?: UserInfo,
  baseUrl?: string,
  fontFamily?: string,
  currentProduct?: LandingProduct,
  products?: Array<LandingProduct>,
  accentDivider?: boolean,
  disabled?: boolean,
  hideStripes?: boolean,
  platformAdmin?: boolean,
}

export const TopBarDesktop = ({
  accentColor,
  logo,
  name,
  user,
  baseUrl,
  fontFamily,
  currentProduct,
  products,
  accentDivider = false,
  disabled = false,
  hideStripes = false,
  platformAdmin
}: TopBarDesktopProps) => {

  const isHydrated = useHydrated()

  const { t } = useTranslation()

  const { isExternalLoginEnabled } = useApiHubPlan()

  const location = useLocation()

  const ProfileComponent = useCallback(props => <Profile user={user || {}} isConsumerSide={true} platformAdmin={platformAdmin} {...props} />, [user, platformAdmin])

  const navigate = useNavigate()

  const { consumerTopBar } = usePageLayout()

  const [searchModalOpen, setSearchModalOpen] = useState(false)

  useEffect(() => {
    if (searchModalOpen) {
      setSearchModalOpen(false)
    }
  }, [location.pathname])

  const onChangeProduct = useCallback(
    (product: LandingProduct) => navigate(`/${product.slug}`),
    []
  )

  const showDebugMenu = isHydrated ? localStorage.getItem('portal.showDebugMenu') === 'true' : false
  
  const isStatusPage = CONSUMER_PATHNAMES.includes(location.pathname)

  return (
    <div>
      <nav
        className={clsx(
          'sp-topbar',
          'w-full flex items-center justify-between h-15 mx-auto bg-(--sp-color-surface) z-50 relative',
          !isStatusPage && 'border-(--sp-color-border-subtle) border-b border-solid'
        )}
        aria-label={t('consumer.topBar.ariaLabel')}
        data-testid="topbar-desktop"
      >
        {disabled && !hideStripes && <div data-testid="bg-stripes" className="absolute w-full h-full bg-stripes bg-(--sp-color-surface-sunken)" />}
        <div className="flex grow-0 shrink-0">
          <Link
            to={baseUrl || '/'}
            className={clsx('sp-topbar-logo px-4 py-1.5 flex items-center duration-200 h-15 transition-color', disabled && 'pointer-events-none')}
            aria-label={t('consumer.topBar.landingPageLink.ariaLabel')}
          >
            {logo ? <img className={clsx('w-auto h-8 max-w-full z-1', disabled && 'opacity-80')} src={logo} alt="logo" /> : <div className="font-semibold text-body1 z-1 h-full line-clamp-2 max-w-125 break-all content-center">{name}</div>}
          </Link>
        </div>
        {
          products?.length && currentProduct?.slug ? (
            <div className="sp-topbar-product-selector grow-0 shrink-0 pl-10">
              <Select
                name="product"
                accentColor={accentColor}
                className="w-54!"
                optionsClassName="sp-topbar-product-selector-options !w-[13.5rem] !max-h-50 rounded"
                optionClassName="!whitespace-pre-wrap !break-all"
                theme="neutral"
                defaultValue={currentProduct}
                options={products}
                onChange={onChangeProduct}
                aria-label={t('consumer.topBar.productSelect.ariaLabel')}
                disabled={disabled}
                buttonClassName={clsx('sp-topbar-product-selector-button', disabled && '!bg-inherit')}
                size="s"
              />
            </div>
          ) : null
        }
        <div className="flex-1" />
        {!consumerTopBar?.search.disabled && (
          <div className="grow-0 shrink-0 w-66 mx-4">
            {isHydrated && <SearchButton className="sp-search-button" onClick={() => setSearchModalOpen(true)} disabled={disabled} />}
          </div>
        )}
        <div className="gap-x-6 flex items-center justify-end grow-0 shrink-0 h-15 z-99">
          {showDebugMenu && <DebugUI featureFlags={Object.values(FeatureFlags)} />}
          {
            user?.email ?
              <Popover
                data-testid="profile-button"
                overlayClassName="-left-[296px] mt-2"
                triggerClassName="outline-none"
                trigger={() => (
                  <IconSegment
                    label={t('consumer.topBar.profile.ariaLabel')}
                    icon={<IconUserCircle width={20} height={20} />}
                    clickable={!disabled}
                    className={clsx(
                      'sp-topbar-profile-button',
                      'h-[3.688rem] bg-(--sp-color-surface) text-(--sp-color-text-muted) hover:bg-(--sp-color-surface-hover)',
                      disabled && 'bg-transparent hover:bg-transparent dark:bg-transparent dark:hover:bg-transparent dark:hover:text-(--sp-color-text-muted) opacity-70',
                    )}
                  />
                )}
                overlay={ProfileComponent}
                disabled={disabled}
              />
              :
              <span className="flex gap-4">
                <DarkModeToggleLoggedOut />
                <LinkButton
                  to={`/services/api/login${isExternalLoginEnabled ? '?external=true' : ''}`}
                  type="secondary"
                  data-testid="login-button"
                  size="m"
                  className={clsx(
                    'sp-login-button',
                    'mr-4 w-26 justify-center border-(--sp-color-accent) dark:border-(--sp-color-accent) hover:bg-(--sp-color-surface-inverse) dark:hover:bg-(--sp-color-surface-inverse) hover:text-(--sp-color-text-inverse) dark:hover:text-(--sp-color-text-inverse) hover:border-transparent! dark:hover:border-transparent!'
                  )}
                  style={{ fontFamily }}
                >
                  {t('components.ui.buttons.logIn.label')}
                </LinkButton>
              </span>

          }
        </div>
      </nav>

      {accentDivider && <div
        data-testid="divider"
        className="sp-accent-divider h-1.5 bg-(--sp-accent-divider-color,var(--sp-color-accent))"
      />}
      <SearchModalDesktop
        isOpen={searchModalOpen}
        onClose={() => setSearchModalOpen(false)} 
        accentColor={accentColor}
        products={products}
        currentProduct={currentProduct}
      />
    </div >
  )
}